import React, {useCallback, useContext} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {
  Backdrop,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import {Banner, Button} from '@zoom/react-ui';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import CancellationContext from './CancellationContext';
import CancellationFormContext from './CancellationFormContext';
import {CancellationsContainer} from './Style';

import {cancelBookedEvent} from 'Api/Calendar';
import ApptCard from 'Components/ApptCard';
import {ZmSecondaryBtn} from 'Components/ZmButtons';
import {ZmTextField} from 'Components/ZmInputs';
import ZmSpinner from 'Components/ZmSpinner';
import {CaptchaError} from 'Error/CaptchaError';
import {HttpError} from 'Error/HttpError';
import useIsAutoRefundLimit from 'hooks/useIsAutoRefundLimit';
import {formatPriceTo2Decimal, htmlEscape} from 'Utils';
import {useQueryWithCaptchaPasscode} from 'Utils/Captcha';
import {CURRENCY_SYMBOL} from 'Utils/consts';


const MAX_CANCEL_REASON_LEN = 300;

const getCancelValidationSchema = () => {
  return yup.object({
    cancelReason: yup
      .string('Reason')
      .max(MAX_CANCEL_REASON_LEN, `Can not be longer than ${MAX_CANCEL_REASON_LEN} characters`)
      .transform((s) => htmlEscape(s)),
  });
};

/**
 * Component to manage cancel form content and submission
 * @param {Object} props
 * @param {String} props.attendeeId attendeeId or old cancellations-formatted id
 * @param {Object} props.eventInfo
 * @param {Function} props.onBackCancel
 * @param {Function} props.onSuccess
 * @return {ReactElement}
 */
export default function CancelFormContent(props) {
  const {
    attendeeId,
    eventInfo,
    onBackCancel,
    onSuccess,
  } = props;
  const {
    timeZone,
  } = useContext(CancellationContext);
  const {
    cancelMessage,
    errorState,
    isCancelInProgress,
    setCancelInProgress,
    setCancelMessage,
    setErrorState,
  } = useContext(CancellationFormContext);
  const {t} = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // merge derived error state with dynamic error state
  const errorInfo = {
    isError: errorState.isError,
    message: errorState.message,
  };

  const {
    formState: {errors},
    handleSubmit,
    register,
  } = useForm({
    resolver: yupResolver(getCancelValidationSchema()),
  });

  const onSubmit = () => {
    setCancelInProgress(true);
    handleSubmit(async (formValues) => {
      try {
        await cancelAppointment(formValues);
      } catch (e) {
        console.error('Encounter unexpected error when canceling appointment:', e);
      } finally {
        setCancelInProgress(false);
      }
    }, () => setCancelInProgress(false))();
  };

  // handle all errors in the component
  const errorHandler = useCallback((error) => {
    let reason = '';
    if (error instanceof CaptchaError) {
      reason = error.message;
    } else if (error instanceof HttpError) {
      switch (error.errorCode) {
        case 400:
          reason = error.body?.error?.message || t('booking.genericHttpValidationError');
          break;
        case 401:
          if (error.message === 'Unauthorized') {
            reason = error.body?.message || 'Unauthorized';
          }
          break;
        case 404:
          reason = t('error.errorMsgBody404');
          break;
        case 500:
          reason = t('error.errorMsgBody500');
          break;
        default:
          reason = 'Something went wrong, please try again later';
          break;
      }
      console.log(error.body);
    } else {
      reason = 'An error occurred';
    }
    setCancelInProgress(false);
    setErrorState({
      isError: true,
      reason,
    });
  }, [setCancelInProgress, setErrorState, t]);

  const cancelEventQueryArgs = useCallback(async (formValues, recaptchaToken) => {
    try {
      setCancelInProgress(true);
      const payload = {recaptchaToken};
      if (cancelMessage) {
        payload.comment = cancelMessage;
      }
      await cancelBookedEvent(attendeeId, payload);
      onSuccess();
    } catch (error) {
      errorHandler(error);
    }
  }, [errorHandler, setCancelInProgress, attendeeId, cancelMessage, onSuccess]);

  const cancelAppointment = useQueryWithCaptchaPasscode(cancelEventQueryArgs);

  const currency = eventInfo.attendees?.[0]?.currency;
  const currencySymbol = CURRENCY_SYMBOL[currency];
  const amount = eventInfo.attendees?.[0]?.totalFee || 0;
  const isPaidEvent = amount > 0;
  const startDateTime = eventInfo.originalEvent?.start.dateTime;

  const isAutoRefundLimit = useIsAutoRefundLimit(startDateTime, timeZone, eventInfo.attendees?.[0].autoRefundPolicy);

  return (
    <CancellationsContainer>
      <ApptCard
        eventInfo={eventInfo}
        timeZone={timeZone}
        displayName={eventInfo?.hostDisplayName}
        guests={eventInfo?.guests}
        showAutoRefundPolicy={true}
      />

      <Backdrop
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          position: 'absolute',
          backgroundColor: 'rgba(255, 255, 255, 0.6)',
        }}
        open={isCancelInProgress}
      >
        <ZmSpinner size="80" absolute />
      </Backdrop>
      {(errorInfo.isError) &&
        <Alert severity="error">{errorInfo.message}</Alert>
      }

      {isPaidEvent && (
        <>
          {isAutoRefundLimit && (
            <Banner
              sx={{marginBottom: '14px'}}
              type="warn"
              content={t('payment.cancellationWarning')}
              closable={false}
            />
          )}
          {!isAutoRefundLimit && (
            <Banner
              sx={{marginBottom: '14px'}}
              type="info"
              content={t('payment.receiveRefund', [`${currencySymbol}${formatPriceTo2Decimal(amount)}`])}
              closable={false}
            />
          )}
        </>
      )}

      <ZmTextField
        label={`${t('cancellation.cancelReasons')} (${t('common.optional')})`}
        autoFocus
        fullWidth
        multiline
        minRows={2}
        maxRows={10}
        value={cancelMessage}
        inputProps={{...register('cancelReason'), maxLength: MAX_CANCEL_REASON_LEN}}
        placeholder={t('apptForm.optionalMessage')}
        onChange={(event) => setCancelMessage(event.target.value)}
        aria-live="polite"
        error={!!errors.cancelReason?.message}
        helperText={errors.cancelReason?.message}
        sx={{marginBottom: '80px'}}
      />
      <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
      }}>
        {!isSmallScreen && <ZmSecondaryBtn
          size="small"
          onClick={onBackCancel}
          sx={{
            marginRight: '8px',
          }}
        >
          {t('common.back')}
        </ZmSecondaryBtn>}
        <Button
          danger
          onClick={onSubmit}
          disabled={isCancelInProgress}
          style={isSmallScreen ? {
            width: '100%',
          }: {}}
        >
          {t('cancellation.cancelAppt')}
        </Button>
      </Box>
    </CancellationsContainer>
  );
}
